/**
 * 3B · O papel do CLIENTE (intermediação): só a parte dele. Os lotes e as
 * pesagens da porteira dele, o valor ao vendedor (com o mesmo financeiro e
 * funrural da ficha, proporcional), os gastos da porteira e a comissão. Nada
 * das outras porteiras. Quem não vê comissão imprime sem ela.
 *
 * R2B (revisão de 06/10/2026): o papel dizia «Total a pagar» sem dizer para
 * quem. Agora são DOIS blocos, cada um com o destinatário, e nenhum total
 * único: «Pague direto ao vendedor <nome>» e «Pague a <empresa>: frete,
 * gastos e comissão» (`pagamentoDoCliente`, exportar.ts — a mesma conta do
 * Excel do cliente). Mora no passo Romaneio, junto com o de compra e o de
 * venda (os três papéis juntos).
 */
import type { ContaDaPorteira } from '../dominio/destinos'
import { linhasDeDesconto, pagamentoDoCliente, periodo, textoDaMedia } from '../dominio/exportar'
import { dataBR, diaDe, hora, kg, num, quando, reais } from '../dominio/numeros'
import { BASES, type Ficha } from '../dominio/tipos'
import { detalheDoRegistro } from './Pesar'

export function PapelDoCliente({ ficha, p, emitente, semComissao }: { ficha: Ficha; p: ContaDaPorteira; emitente: string; semComissao: boolean }) {
  const destino = [p.porteira?.municipio, p.porteira?.uf].filter(Boolean).join('/')
  const pc = pagamentoDoCliente(ficha, p, semComissao, emitente)
  return (
    <div className="papel">
      <div className="p-cabeca">
        <div><h3>Romaneio e fechamento do cliente</h3><div className="p-sub">{emitente}</div></div>
        <div className="p-numero"><b>Ficha {ficha.numero}</b><br />{periodo(ficha)}</div>
      </div>
      {/* 4.2 (gestor, 06/10/2026): as pontas não se comunicam — para o cliente o
          vendedor é o gestor; o produtor de origem não aparece neste papel */}
      <div className="p-linha"><b>Vendedor:</b> {pc.empresa}</div>
      <div className="p-linha"><b>Comprador:</b> {p.nome}{destino ? ` · ${destino}` : ''}</div>
      {/* 3D: só a GTA da porteira dele — cada destino tem a sua */}
      {p.porteira?.gta?.trim() && <div className="p-linha"><b>GTA nº</b> {p.porteira.gta.trim()}</div>}

      <h4>Animais do cliente</h4>
      <table>
        <thead><tr>
          <th>Lote</th><th className="n">Cab.</th><th className="n">Peso total (kg)</th><th className="n">Média (kg)</th>
          {p.temArroba && <><th className="n">Rend.</th><th className="n">Arrobas</th></>}
          {p.temPreco && <><th className="n">Preço</th><th className="n">Valor</th></>}
        </tr></thead>
        <tbody>{p.linhas.map((l) => (
          <tr key={l.lote.id}>
            <td>{l.lote.categoria} <span className="p-base">{BASES[l.lote.base].nome.toLowerCase()}</span></td>
            <td className="n">{l.cab}</td><td className="n">{l.kg ? kg(l.kg) : '—'}</td><td className="n">{textoDaMedia(l.kg, l.cab, l.cabPesadas)?.replace(' kg', '') ?? '—'}</td>
            {p.temArroba && <><td className="n">{l.lote.base === 'arroba' ? num(l.lote.rendimento, 1) + '%' : ''}</td><td className="n">{l.arrobas === null ? '' : num(l.arrobas, 2)}</td></>}
            {p.temPreco && <><td className="n">{l.lote.preco ? `${num(l.lote.preco, 2)} ${BASES[l.lote.base].unidade.replace('R$', '').trim()}` : '—'}</td><td className="n">{reais(l.valor)}</td></>}
          </tr>
        ))}</tbody>
        <tfoot><tr>
          <th>Total</th><th className="n">{p.cab}</th><th className="n">{p.kg ? kg(p.kg) : '—'}</th><th className="n">{p.media === null ? '—' : num(p.media, 1)}</th>
          {p.temArroba && <><th /><th className="n">{num(p.arrobas, 2)}</th></>}
          {p.temPreco && <><th /><th className="n">{reais(p.bruto)}</th></>}
        </tr></tfoot>
      </table>

      {/* R2B: um pagamento por destinatário — sem total único. O frete da porteira o cliente paga direto (06/10) */}
      <h4>1. {pc.aoVendedor.titulo}</h4>
      <table className="p-valores" style={{ minWidth: '60%' }}><tbody>
        {p.temPreco && <tr><td>Valor bruto</td><td className="n">{reais(p.bruto)}</td></tr>}
        {linhasDeDesconto(p).map((d) => <tr key={d.rotulo}><td>{d.rotulo}</td><td className="n">− {reais(d.valor)}</td></tr>)}
        <tr className="liquido"><td>Valor do gado</td><td className="n">{reais(pc.aoVendedor.valor)}</td></tr>
      </tbody></table>

      {pc.direto.linhas.length > 0 && <>
        <h4>2. {pc.direto.titulo}</h4>
        <table className="p-valores" style={{ minWidth: '60%' }}><tbody>
          {pc.direto.linhas.map((x) => <tr key={x.rotulo}><td>{x.rotulo}</td><td className="n">{reais(x.valor)}</td></tr>)}
          <tr className="liquido"><td>Pague direto (total)</td><td className="n">{reais(pc.direto.valor)}</td></tr>
        </tbody></table>
      </>}

      <h4>{pc.direto.linhas.length > 0 ? 3 : 2}. {pc.aEmpresa.titulo}</h4>
      <table className="p-valores" style={{ minWidth: '60%' }}><tbody>
        {pc.aEmpresa.linhas.map((x) => <tr key={x.rotulo}><td>{x.rotulo}</td><td className="n">{reais(x.valor)}</td></tr>)}
        {!pc.aEmpresa.linhas.length && <tr><td>Nada a pagar a {pc.empresa} nesta porteira.</td><td className="n">{reais(0)}</td></tr>}
        <tr className="liquido"><td>Pague a {pc.empresa}</td><td className="n">{reais(pc.aEmpresa.valor)}</td></tr>
      </tbody></table>
      {!semComissao && !p.comissaoReceber && <div className="p-nota">Comissão ainda não lançada: fora do bloco 2.</div>}

      <h4>Relação de pesagens ({p.registros.length} registro{p.registros.length === 1 ? '' : 's'})</h4>
      <table className="p-itens">
        <thead><tr><th>#</th><th>Data</th><th>Lote</th><th className="n">Cab.</th><th className="n">Peso (kg)</th><th className="n">Média (kg)</th><th>Observação</th></tr></thead>
        <tbody>{p.registros.map((x) => {
          const lote = ficha.lotes.find((l) => l.id === x.loteId)
          return (
            <tr key={x.id}>
              <td>{x.n}</td><td>{dataBR(diaDe(x.em)).slice(0, 5)} {hora(x.em)}</td><td>{lote?.categoria ?? ''}</td>
              <td className="n">{x.pendente ? '—' : x.qtd}</td><td className="n">{x.kg === null ? '—' : kg(x.kg)}</td>
              <td className="n">{x.kg && x.qtd ? num(x.kg / x.qtd, 1) : '—'}</td><td>{detalheDoRegistro(x)}</td>
            </tr>
          )
        })}</tbody>
      </table>
      <div className="p-rodape">Parte do cliente na ficha {ficha.numero}. Os valores ao vendedor são a parte destes animais no romaneio da compra.
        Gerado em {quando(new Date().toISOString())}.</div>
    </div>
  )
}
